/**
 * offer-tiers.js — live "spend more, save more" ladder, ported from the Hydrogen
 * storefront's OrderTierBar. Reads the tier rows the Liquid rendered, fetches the
 * live cart subtotal from /cart.js, and fills the animated ticker, the medal
 * milestone bar, the caption and the table's live column. Runs on the product
 * page, the cart page and the cart drawer; it re-reads the cart whenever the
 * theme re-renders a cart section, and uses event delegation so re-rendered
 * panels keep working without re-binding.
 */
(function () {
  'use strict';
  if (window.__offerTiersInit) return;
  window.__offerTiersInit = true;

  // Seed from the server-rendered cart so the first render matches the tiles
  // Liquid already painted; otherwise unlocked tiles flash back to locked until
  // /cart.js returns (slow on mobile).
  var seedEl = document.querySelector('[data-offer-tiers][data-subtotal]');
  var subtotal = seedEl ? (parseInt(seedEl.getAttribute('data-subtotal'), 10) || 0) / 100 : 0; // dollars
  var loaded = false;
  var openByPlace = {}; // remember expand state per placement across re-renders

  // Presentment currency + shop->presentment rate (Shopify exposes this).
  // The tier thresholds are authored in the shop currency (USD); everything the
  // shopper sees and every comparison must be in their currency.
  // Write only when the value changes (no needless DOM mutations / restyles).
  function setT(el, v) { if (el && el.textContent !== v) el.textContent = v; }
  function setH(el, v) { if (el && el._h !== v) { el.innerHTML = v; el._h = v; } }

  function cur() {
    var c = (window.Shopify && window.Shopify.currency) || {};
    var r = parseFloat(c.rate);
    return { rate: r && r > 0 ? r : 1, iso: c.active || 'USD' };
  }
  // n is an amount ALREADY in the presentment currency.
  // Formatters are cached: building an Intl.NumberFormat is expensive and render()
  // formats dozens of amounts each time.
  var fmtCache = {};
  function fmt(n) {
    var iso = cur().iso;
    try {
      var whole = n % 1 === 0;
      var key = iso + (whole ? '0' : '2');
      // narrowSymbol -> "$50" not "US$50" (some device locales prefix the
      // country code with the default currency symbol).
      var f = fmtCache[key] || (fmtCache[key] = new Intl.NumberFormat(undefined, { style: 'currency', currency: iso, currencyDisplay: 'narrowSymbol', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }));
      return f.format(n);
    } catch (e) {
      return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
    }
  }
  // Reformat any "$N" inside a static string into the presentment currency.
  function reCur(text) {
    var rate = cur().rate;
    return String(text).replace(/\$(\d+(?:\.\d+)?)/g, function (_, num) { return fmt(parseFloat(num) * rate); });
  }

  // Localized strings the Liquid put on the root as a JSON blob, cached per
  // element. English fallbacks keep the bar working if the attribute is absent.
  function strOf(root) {
    if (root._offerStrings) return root._offerStrings;
    var s = {};
    try { s = JSON.parse(root.getAttribute('data-strings') || '{}'); } catch (e) {}
    // Liquid's `| t` HTML-escapes markup for non-_html keys, so a value like
    // "saving <b>{{ value }}</b>" arrives as "saving &lt;b&gt;{{ value }}&lt;/b&gt;"
    // and prints its tags literally when set via innerHTML. Decode entities once
    // so the intended <b> renders. Plain strings pass through unchanged.
    var dec = document.createElement('textarea');
    for (var k in s) { if (typeof s[k] === 'string') { dec.innerHTML = s[k]; s[k] = dec.value; } }
    root._offerStrings = s;
    return s;
  }

  // Fills {{ name }} placeholders in a localized template.
  function tpl(t, map) {
    return String(t).replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, k) {
      return map[k] != null ? map[k] : '';
    });
  }

  function tiersOf(root) {
    var rate = cur().rate;
    return Array.prototype.map.call(root.querySelectorAll('.offer-row'), function (row) {
      return {
        // Converted to the presentment currency so comparisons against the
        // (presentment) cart subtotal are correct.
        min: parseFloat(row.getAttribute('data-min')) * rate,
        value: parseFloat(row.getAttribute('data-value')) * rate,
        row: row,
      };
    });
  }

  function bestTier(tiers) {
    var best = null;
    tiers.forEach(function (t) {
      if (subtotal >= t.min && (!best || t.value > best.value)) best = t;
    });
    return best;
  }

  function nextTier(tiers) {
    var up = tiers
      .filter(function (t) { return t.min > subtotal; })
      .sort(function (a, b) { return a.min - b.min; })[0];
    return up || null;
  }

  var CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 4 4 10-10"/></svg>';
  var LOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';

  function render(root) {
    var place = root.getAttribute('data-place') || 'pdp';
    var freeShip = (parseFloat(root.getAttribute('data-freeship')) || 75) * cur().rate;
    var live = subtotal > 0;
    var tiers = tiersOf(root);
    var best = live ? bestTier(tiers) : null;
    var next = live ? nextTier(tiers) : null;
    var S = strOf(root);

    root.classList.toggle('is-live', live);

    // Localise the static amounts to the shopper's currency (thresholds are
    // authored in USD; tiers[] is already converted).
    tiers.forEach(function (t) {
      var mEl = t.row.querySelector('.offer-row__min'); setT(mEl, fmt(t.min) + '+');
      var vEl = t.row.querySelector('.offer-row__val'); setT(vEl, fmt(t.value) + ' ' + (S.off || 'off'));
    });
    var noteEl = root.querySelector('.offer-note');
    if (noteEl) { if (!noteEl._orig) noteEl._orig = noteEl.textContent; setT(noteEl, reCur(noteEl._orig)); }

    // Ticker lines: teaser + live nudge.
    var ticker = root.querySelector('[data-offer-ticker]');
    var teaser = ticker ? reCur(ticker.getAttribute('data-teaser') || '') : '';
    var nudge = null;
    if (live) {
      if (next) nudge = tpl(S.just_more || 'Just {{ amount }} more for {{ value }} off', { amount: fmt(next.min - subtotal), value: fmt(next.value) });
      else if (best) nudge = tpl(S.biggest || 'Biggest saving unlocked: {{ value }} off', { value: fmt(best.value) });
    }
    // Empty-cart hook: lead with an actionable "Spend X, save Y" using the first
    // tier, so the compact collapsed bar always shows a reason to open it.
    var startHook = (!live && tiers.length)
      ? tpl(S.start_hook || 'Spend {{ amount }}, save {{ value }}', { amount: fmt(tiers[0].min), value: fmt(tiers[0].value) })
      : null;
    // One static line only: the live "add X to reach the next tier" nudge (or the
    // biggest-unlocked / empty-cart hook). No teaser, no rotation.
    var single = live ? (nudge || startHook || teaser) : (startHook || teaser);
    // Cart drawer + cart page: a fixed headline with the biggest saving on the ladder
    // plus free shipping. The live "add X more" amount sits just below (free-shipping
    // line) and in the cart's add-on row, so the bar does not repeat it.
    var place = root.getAttribute('data-place');
    var lines = [single];
    if (place === 'pdp' && tiers.length) {
      // Product page: the live nudge first, then (once a tier is unlocked) what is
      // saved so far, then the biggest saving on offer; animated like the drawer.
      var maxValP = tiers.reduce(function (m, t) { return Math.max(m, t.value); }, 0);
      var upToP = tpl(S.up_to || 'Save up to <b>{{ value }}</b> + Free shipping', { value: fmt(maxValP) });
      lines = [single];
      if (best && next) lines.push(tpl(S.saved_so_far || 'You\u2019ve saved <b>{{ value }}</b> so far, get more', { value: fmt(best.value) }));
      // What adding THIS item would unlock (its price on top of the real cart).
      var itemPrice = (parseFloat(root.getAttribute('data-item-price')) || 0) / 100;
      if (itemPrice > 0) {
        var after = subtotal + itemPrice, reach = null;
        tiers.forEach(function (t) { if (after >= t.min && (!reach || t.min > reach.min)) reach = t; });
        if (reach && (!best || reach.min > best.min)) {
          var shipToo = subtotal < freeShip && after >= freeShip;
          lines.unshift(tpl(S.add_this || 'Add this to unlock <b>{{ value }} off</b>{{ ship }}', { value: fmt(reach.value), ship: shipToo ? ' + free shipping' : '' }));
        }
      }
      if (single !== upToP) lines.push(upToP);
      // Empty cart: also name the idea for first-time visitors (replaced by the
      // specific numbers once something is in the bag).
      if (!live) lines.push(S.spend_more || 'Spend more, save more');
    }
    if ((place === 'drawer' || place === 'cart') && tiers.length) {
      var maxVal = tiers.reduce(function (m, t) { return Math.max(m, t.value); }, 0);
      var upTo = tpl(S.up_to || 'Save up to <b>{{ value }}</b> + Free shipping', { value: fmt(maxVal) });
      // Once a tier is unlocked the headline alternates (animated): what they have
      // saved so far, then the biggest saving on offer.
      lines = best
        ? [tpl(S.saved_so_far || 'You\u2019ve saved <b>{{ value }}</b> so far, get more', { value: fmt(best.value) }), upTo]
        : [upTo];
      single = lines[0];
    }
    // Re-renders happen often (every cart refresh); keep the rotation where it is
    // unless the lines themselves changed, or it would never get past the first.
    var key = lines.join('|');
    if (root._offerKey !== key) {
      root._offerKey = key;
      root._offerLines = lines;
      root._offerIdx = 0;
      setTickerText(root, single);
    }

    // Milestone bar.
    var prev = 0;
    Array.prototype.forEach.call(root.querySelectorAll('.offer-ms'), function (ms) {
      prev = 0;
      Array.prototype.forEach.call(ms.querySelectorAll('.offer-ms__fill'), function (fill) {
        var min = parseFloat(fill.getAttribute('data-min'));
        var pmin = parseFloat(fill.getAttribute('data-prevmin')) || 0;
        var pct = !live ? 0 : subtotal >= min ? 100 : subtotal <= pmin ? 0 : ((subtotal - pmin) / (min - pmin)) * 100;
        fill.style.width = pct + '%';
      });
      Array.prototype.forEach.call(ms.querySelectorAll('.offer-ms__medal'), function (medal, idx) {
        var min = parseFloat(medal.getAttribute('data-min'));
        medal.classList.remove('is-preview', 'is-unlocked', 'is-next', 'is-locked');
        // Empty cart: the first tier is the target (its star spins); the rest wait.
        var state = !live
          ? (idx === 0 ? 'next' : 'locked')
          : subtotal >= min ? 'unlocked' : (next && next.min === min) ? 'next' : 'locked';
        medal.classList.add('is-' + state);
      });
    });

    // PDP price ladder: localise the amounts and mark each tier tile
    // unlocked / next / locked (empty cart: the first tier is the target).
    Array.prototype.forEach.call(root.querySelectorAll('[data-lad-amt]'), function (el) {
      setT(el, (el.getAttribute('data-lad-prefix') || '') + fmt(parseFloat(el.getAttribute('data-lad-amt')) * cur().rate) + (el.getAttribute('data-lad-suffix') || ''));
    });
    Array.prototype.forEach.call(root.querySelectorAll('.offer-lad [data-min]'), function (tile) {
      var m = parseFloat(tile.getAttribute('data-min')) * cur().rate;
      var first = tiers.length ? tiers[0].min : m;
      var st = !live
        ? (Math.abs(m - first) < 0.01 ? 'next' : 'locked')
        : subtotal >= m ? 'unlocked' : (next && Math.abs(next.min - m) < 0.01) ? 'next' : 'locked';
      // Only touch the tile when its state really changes: removing and re-adding
      // the same class restarts colour transitions on some phones (Android
      // Chrome), which read as the tile backgrounds jumping.
      if (!tile.classList.contains('is-' + st)) {
        tile.classList.remove('is-unlocked', 'is-next', 'is-locked');
        tile.classList.add('is-' + st);
      }
      // Active tile: how far from the previous tier to this one.
      if (st === 'next') {
        var idx = Array.prototype.indexOf.call(tile.parentElement.children, tile);
        var prevMin = idx > 0 ? parseFloat(tile.parentElement.children[idx - 1].getAttribute('data-min')) * cur().rate : 0;
        var pct = live ? Math.max(0, Math.min(100, ((subtotal - prevMin) / (m - prevMin)) * 100)) : 0;
        var pv = pct.toFixed(1) + '%';
        if (tile.style.getPropertyValue('--p') !== pv) tile.style.setProperty('--p', pv);
      } else if (tile.style.getPropertyValue('--p')) {
        tile.style.removeProperty('--p');
      }
    });

    // Collapsed caption (cart / drawer). Hidden when the full panel is open, so
    // the two never show at once.
    var collapsed = root.querySelector('[data-offer-collapsed]');
    var panelEl = root.querySelector('[data-offer-panel]');
    root._offerLive = live;
    if (collapsed) collapsed.hidden = !live || (panelEl && !panelEl.hidden);
    var cap = root.querySelector('[data-offer-caption]');
    if (cap) {
      if (subtotal >= freeShip) {
        setH(cap, best
          ? tpl(S.freeship_saving || 'Free shipping unlocked · saving <b>{{ value }}</b>', { value: fmt(best.value) })
          : (S.freeship || 'Free shipping unlocked'));
      } else {
        setH(cap, tpl(S.away_freeship || '<b>{{ amount }}</b> away from free shipping', { amount: fmt(freeShip - subtotal) }));
      }
    }

    // PDP savings line.
    var sav = root.querySelector('[data-offer-savings]');
    if (sav) {
      if (!live) setH(sav, S.caption || 'Each level unlocks a bigger reward as your order grows.');
      else if (best) setH(sav, tpl(S.saving_so_far || 'You’re saving <b>{{ value }}</b> so far', { value: fmt(best.value) }));
      else setH(sav, tpl(S.add_to_start || 'Add <b>{{ amount }}</b> to start saving', { amount: fmt(tiers[0].min - subtotal) }));
    }

    // Table / card live column + applied / next highlight.
    var firstMin = tiers.length ? tiers[0].min : null;
    tiers.forEach(function (t) {
      var cell = t.row.querySelector('[data-offer-live]');
      var applied = best && best.min === t.min;
      // "next" is the tier to aim for: the next locked one when live, else the
      // first tier (the one that starts the savings) when the cart is empty.
      var isNext = live ? (next && next.min === t.min) : (t.min === firstMin);
      var earned = live && subtotal >= t.min;
      t.row.classList.toggle('is-applied', !!applied);
      // Earned but not the single applied tier (applied gets the solid fill).
      t.row.classList.toggle('is-earned', earned && !applied);
      t.row.classList.toggle('is-next', !!isNext);
      // Leading status icon (cards only): lock while out of reach, check once earned.
      var ico = t.row.querySelector('[data-offer-ico]');
      setH(ico, (applied || earned) ? CHECK : LOCK);
      if (!cell) return;
      // Cards carry the leading lock/check icon, so their live column shows the
      // amount only (no duplicate icon). The cart/drawer table keeps lock/check.
      var isCard = t.row.classList.contains('offer-card');
      if (!live) { setH(cell, ''); return; }
      if (applied) setH(cell, '<span class="offer-live__applied">−' + fmt(t.value) + '</span>');
      else if (subtotal >= t.min) setH(cell, isCard ? '' : CHECK);
      else setH(cell, isCard ? ('+' + fmt(t.min - subtotal)) : (LOCK + '+' + fmt(t.min - subtotal)));
    });

    // Carousel arrow visibility (PDP).
    updateArrows(root);
  }

  // Both arrows stay visible; dim + disable them at the track ends. RTL-safe
  // (scrollLeft is negative in a right-to-left scroller, so compare on |scrollLeft|).
  function updateArrows(root) {
    var track = root.querySelector('[data-offer-track]');
    var prev = root.querySelector('[data-offer-prev]');
    var next = root.querySelector('[data-offer-next]');
    if (!track) return;
    var sl = Math.abs(track.scrollLeft);
    var max = track.scrollWidth - track.clientWidth - 2;
    if (prev) prev.disabled = sl <= 2;
    if (next) next.disabled = sl >= max || max <= 0;
  }

  function setTickerText(root, text) {
    var line = root.querySelector('[data-offer-ticker-line]');
    if (!line || line._offerText === text) return;
    line._offerText = text;
    line.innerHTML = text; // our own template strings; amounts come from fmt()
    // Wherever the headline alternates between lines (drawer, cart page, product
    // page), the swap slides in.
    if ((root._offerLines || []).length > 1) {
      line.classList.remove('is-sliding');
      void line.offsetWidth; // restart the animation
      line.classList.add('is-sliding');
    }
  }

  function renderAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-offer-tiers]'), function (root) {
      // restore remembered open state after a section re-render
      var place = root.getAttribute('data-place') || 'pdp';
      if (openByPlace[place]) applyOpen(root, true);
      // Keep the carousel arrows in sync as the shopper scrolls it.
      var track = root.querySelector('[data-offer-track]');
      if (track && !track._offerBound) {
        track._offerBound = true;
        track.addEventListener('scroll', function () { updateArrows(root); }, { passive: true });
      }
      render(root);
      startTicker(root);
    });
  }

  function applyOpen(root, open) {
    // Cart / drawer only — the PDP uses a carousel with no expand toggle.
    var btn = root.querySelector('[data-offer-toggle]');
    var panel = root.querySelector('[data-offer-panel]');
    var label = root.querySelector('[data-offer-toggle-label]');
    var collapsed = root.querySelector('[data-offer-collapsed]');
    var S = strOf(root);
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.hidden = !open;
    // When open, hide the collapsed progress bar; when closed, show it if live.
    if (collapsed) collapsed.hidden = open || !root._offerLive;
    setT(label, open ? (S.hide || 'Hide') : (S.unlock || 'View offers'));
    openByPlace[root.getAttribute('data-place') || 'pdp'] = open;
  }

  function startTicker(root) {
    if (root._offerTicker) return; // already running on this element
    root._offerIdx = root._offerIdx || 0;
    root._offerTicker = setInterval(function () {
      if (!document.body.contains(root)) { clearInterval(root._offerTicker); return; }
      var lines = root._offerLines || [];
      if (lines.length < 2) return;
      root._offerIdx = (root._offerIdx + 1) % lines.length;
      setTickerText(root, lines[root._offerIdx]);
    }, 3400);
  }

  // ---- Tier-crossing celebration -----------------------------------------
  // When the cart subtotal crosses UP into a new saving tier, fire a brief,
  // premium confetti moment + a message ("You unlocked $X off, add $Y more ...").
  // Once per tier per session; never on a cart that already qualified at page
  // load (a returning shopper); skipped for prefers-reduced-motion (message
  // only). The amounts are the real automatic "Spend and save" discount, so the
  // claim is honest.
  var offerBaselined = false;
  var celebrating = false;
  // Tier min at the PREVIOUS cart read, in memory, page-scoped. It tracks the
  // cart both UP and DOWN: when a shopper removes items and drops below a tier,
  // this falls too, so climbing back into that tier celebrates again. That also
  // makes the moment re-testable (empty cart, re-add) instead of a one-shot per
  // session. Same-subtotal reads (our own toast/canvas DOM writes retrigger the
  // cart mutation observer) have bestMin == prev, so they never re-fire.
  var lastTierMin = 0;

  function maybeCelebrate() {
    if (celebrating) return;
    var root = document.querySelector('[data-offer-tiers]');
    if (!root || !loaded) return;
    var tiers = tiersOf(root);
    if (!tiers.length) return;
    var best = bestTier(tiers);
    var bestMin = best ? best.min : 0;
    var prev = lastTierMin;
    lastTierMin = bestMin;
    // First read of the page is a baseline, not a crossing (a returning shopper
    // whose cart already qualifies must not get confetti on load).
    if (!offerBaselined) { offerBaselined = true; return; }
    // Fire only when the cart has just climbed INTO a higher tier.
    if (best && bestMin > prev) celebrateWhenDrawerOpen(root, best, nextTier(tiers));
  }

  // An add re-reads the cart (fetch hook) before the drawer has opened, so the
  // confetti used to play on the page and THEN the drawer slid in over it. When
  // a cart drawer exists but is still closed, wait for it to open (up to 2.5s)
  // and celebrate inside it; with no drawer (cart page, drawer off) fire at once.
  function celebrateWhenDrawerOpen(root, best, next) {
    var d = document.querySelector('dialog.cart-drawer');
    if (!d || d.open || /\/cart\/?$/.test(location.pathname)) { celebrate(root, best, next); return; }
    celebrating = true; // hold the slot while we wait
    var done = false;
    var go = function () {
      if (done) return; done = true;
      mo.disconnect(); clearTimeout(timer);
      celebrating = false;
      // A moment after it opens, so the slide-in has finished.
      setTimeout(function () { celebrate(root, best, next); }, d.open ? 280 : 0);
    };
    var mo = new MutationObserver(function () { if (d.open) go(); });
    mo.observe(d, { attributes: true, attributeFilter: ['open'] });
    var timer = setTimeout(go, 2500);
  }

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  function celebrate(root, best, next) {
    var S = strOf(root);
    var title = tpl(S.earned || 'You unlocked {{ value }} off', { value: fmt(best.value) });
    var sub = next
      ? tpl(S.earned_next || 'Add {{ amount }} more to save {{ value }}', { amount: fmt(Math.max(0, next.min - subtotal)), value: fmt(next.value) })
      : (S.earned_top || 'That is your biggest saving. Nice one.');
    // Mount over the cart drawer when it is open (a modal <dialog> lives in the
    // top layer, so a body-level toast would render BEHIND it); full screen
    // otherwise (cart page, PDP).
    var host = document.querySelector('dialog[open]') || document.body;
    celebrating = true;
    // Release the lock once the moment has fully played out (confetti 2s +
    // toast auto-dismiss 5.5s) so a LATER, higher tier can still celebrate.
    setTimeout(function () { celebrating = false; }, 6000);
    offerToast(host, title, sub);
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) offerConfetti(host);
  }

  function offerToast(host, title, sub) {
    var inDialog = host.nodeName === 'DIALOG';
    var old = document.getElementById('offer-celebrate'); if (old) old.remove();
    var el = document.createElement('div');
    el.id = 'offer-celebrate';
    el.setAttribute('role', 'status');
    var place = inDialog
      ? 'position:absolute;left:50%;top:14%;transform:translate(-50%,-10px)'
      : 'position:fixed;left:50%;top:20%;transform:translate(-50%,-10px)';
    // The panel does not take clicks (pointer-events:none) so it never blocks the
    // drawer; only the close button re-enables pointer events, so a shopper can
    // dismiss early without the toast getting in the way.
    el.style.cssText = place + ';z-index:2147483000;pointer-events:none;max-width:min(88%,360px);padding:18px 26px 18px 22px;text-align:center;background:#fff;color:#2a251f;border:1px solid rgba(42,37,31,.12);border-radius:14px;box-shadow:0 26px 64px -26px rgba(42,37,31,.5);opacity:0;transition:opacity .35s ease,transform .35s ease;font-family:inherit';
    el.innerHTML = '<button type="button" data-offer-celebrate-close aria-label="Close" style="position:absolute;top:5px;right:7px;width:26px;height:26px;padding:0;border:0;background:none;color:rgba(42,37,31,.45);font-size:20px;line-height:1;cursor:pointer;pointer-events:auto">×</button>'
      + '<div style="font-size:1.6rem;line-height:1">🎉</div>'
      + '<div style="margin-top:8px;font-weight:700;font-size:1.05rem;color:#c04a63">' + esc(title) + '</div>'
      + (sub ? '<div style="margin-top:5px;font-size:.9rem;color:rgba(42,37,31,.72)">' + esc(sub) + '</div>' : '');
    host.appendChild(el);
    requestAnimationFrame(function () { el.style.opacity = '1'; el.style.transform = 'translate(-50%,0)'; });
    var timer;
    function dismiss() {
      if (!el.parentNode) return;
      clearTimeout(timer);
      el.style.opacity = '0'; el.style.transform = 'translate(-50%,-10px)';
      setTimeout(function () { if (el.parentNode) el.remove(); }, 420);
    }
    timer = setTimeout(dismiss, 5500);
    var x = el.querySelector('[data-offer-celebrate-close]');
    if (x) x.addEventListener('click', dismiss);
  }

  function offerConfetti(host) {
    // A modal <dialog> sits ABOVE a body-level popover in the top layer, so a
    // full-screen canvas on <body> renders BEHIND the drawer panel. Mount the
    // canvas INSIDE the open drawer instead: it then paints over the drawer's
    // own content (the point of the moment). When no drawer is open (cart page,
    // PDP) it falls back to a full-screen fixed canvas on <body>.
    var inDialog = host && host.nodeName === 'DIALOG';
    var W = inDialog ? host.clientWidth : window.innerWidth;
    var H = inDialog ? host.clientHeight : window.innerHeight;
    var old = document.getElementById('offer-confetti'); if (old) old.remove();
    var cv = document.createElement('canvas');
    cv.id = 'offer-confetti';
    cv.style.cssText = (inDialog ? 'position:absolute;left:0;top:0' : 'position:fixed;inset:0') +
      ';margin:0;padding:0;border:0;background:transparent;width:' + W + 'px;height:' + H + 'px;z-index:2147482998;pointer-events:none';
    host.appendChild(cv);
    var ctx = cv.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = W * dpr; cv.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    var colors = ['#c04a63', '#d4a24e', '#d17a8d', '#8a7c66', '#f6f1e9'];
    var cx = W / 2, cy = H * 0.30;
    var parts = [];
    for (var i = 0; i < 140; i++) {
      var ang = Math.random() * Math.PI * 2;
      var speed = 3 + Math.random() * 11;
      parts.push({
        x: cx + (Math.random() - 0.5) * 40, y: cy,
        vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed - 5,
        w: 5 + Math.random() * 6, h: 8 + Math.random() * 8,
        rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.35,
        color: colors[(Math.random() * colors.length) | 0]
      });
    }
    var start = performance.now(), DUR = 2000;
    function frame(now) {
      var t = now - start;
      ctx.clearRect(0, 0, W, H);
      for (var j = 0; j < parts.length; j++) {
        var p = parts[j];
        p.vy += 0.22; p.vx *= 0.992; p.x += p.vx; p.y += p.vy; p.rot += p.vr;
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - t / DUR);
        ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.color; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (t < DUR) requestAnimationFrame(frame);
      else cv.remove();
    }
    requestAnimationFrame(frame);
  }

  function fetchCart() {
    // Cache-bust: browsers/CDN can serve a stale /cart.js, so right after an
    // add the subtotal would read OLD and no tier crossing is ever detected.
    // A unique query + no-store forces the live cart every time.
    fetch('/cart.js?t=' + Date.now(), { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        subtotal = (c.items_subtotal_price || 0) / 100;
        loaded = true;
        try { renderAll(); } catch (e) {}
        maybeCelebrate();
      })
      .catch(function () { renderAll(); });
  }

  // Expand / collapse via delegation (survives cart section re-renders).
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    // The chip toggles (open <-> close).
    var btn = e.target.closest('[data-offer-toggle]');
    if (btn) {
      var root = btn.closest('[data-offer-tiers]');
      if (root) applyOpen(root, btn.getAttribute('aria-expanded') !== 'true');
      return;
    }
    // Clicking anywhere on the collapsed bar opens it (bigger tap target). When
    // already open, do nothing here — the chip is how you collapse. (Cart/drawer;
    // the PDP head has no panel to open — it uses the card carousel.)
    var head = e.target.closest('.offer-tiers__head');
    if (head) {
      var root2 = head.closest('[data-offer-tiers]');
      var panel = root2 && root2.querySelector('[data-offer-panel]');
      if (panel && panel.hidden) applyOpen(root2, true);
    }
    // Carousel arrows (they live in the head, so scope to the tiers root).
    var arrow = e.target.closest('[data-offer-prev], [data-offer-next]');
    if (arrow) {
      var aroot = arrow.closest('[data-offer-tiers]');
      var track = aroot && aroot.querySelector('[data-offer-track]');
      if (track) {
        var dir = arrow.hasAttribute('data-offer-next') ? 1 : -1;
        track.scrollBy({ left: dir * Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
      }
    }
  });

  // Re-read the cart whenever the theme changes it via a known event.
  ['cart:refresh', 'cart:rendered', 'cart:updated', 'cart:change', 'cart:build'].forEach(function (ev) {
    document.addEventListener(ev, fetchCart);
  });
  // The drawer opening is a good moment to re-sync.
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[href="#cart"], [data-cart-open], .header__cart, [aria-controls*="cart" i]')) {
      setTimeout(fetchCart, 400);
    }
  });

  // The cart page and drawer re-render their line items by swapping the innerHTML
  // of [data-cart-contents]. The theme's quantity / remove / add flow does this
  // directly (no 'cart:refresh' event), and our panel is a SIBLING of that host,
  // so it is never touched and would show stale totals until a reload. Watch the
  // host for any change and re-read the cart. Debounced (a swap fires many
  // mutations), and safe from loops because our own writes land on the offer root
  // OUTSIDE [data-cart-contents], so they never retrigger this observer.
  function watchCart() {
    var hosts = document.querySelectorAll('[data-cart-contents]');
    if (!hosts.length || window.__offerCartObserver) return;
    var t = null;
    // Ignore our own writes: the drawer's tier panel lives inside the cart host,
    // and re-reading the cart after each of its renders looped forever (~3x/s),
    // restyling every tile and eating taps on phones.
    var mo = new MutationObserver(function (records) {
      var real = records.some(function (r) {
        var n = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        return !(n && n.closest && n.closest('[data-offer-tiers]'));
      });
      if (!real) return;
      clearTimeout(t);
      t = setTimeout(fetchCart, 120);
    });
    hosts.forEach(function (h) { mo.observe(h, { childList: true, subtree: true }); });
    window.__offerCartObserver = mo;
  }

  // Most reliable cart-change signal: wrap fetch. Every AJAX cart mutation the
  // theme makes goes through /cart/add|change|update|clear, so this fires even
  // when the drawer re-renders and detaches the node watchCart() observed, and
  // even when the theme dispatches none of the events above. Debounced.
  var syncT = null;
  function scheduleSync() { clearTimeout(syncT); syncT = setTimeout(fetchCart, 160); }
  (function () {
    if (window.__offerFetchPatched || !window.fetch) return;
    window.__offerFetchPatched = true;
    var orig = window.fetch;
    window.fetch = function (input) {
      var url = '';
      try { url = (typeof input === 'string' ? input : (input && input.url)) || ''; } catch (e) {}
      var p = orig.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i.test(url)) {
        try { p.then(scheduleSync, function () {}); } catch (e) {}
      }
      return p;
    };
  })();

  // The drawer flipping open is another good moment to re-sync (covers the add
  // that opens the drawer, whatever code path added the item).
  function watchDrawer() {
    var d = document.querySelector('dialog.cart-drawer');
    if (!d || window.__offerDrawerObserver) return;
    var mo = new MutationObserver(function () { if (d.open) scheduleSync(); });
    mo.observe(d, { attributes: true, attributeFilter: ['open'] });
    window.__offerDrawerObserver = mo;
  }

  // Tile details popover (PDP ladder). One popover per panel, positioned under
  // the row with its caret on the tapped tile; filled from the tile and the
  // live cart. Copy mirrors the real discount (one tier applies, automatic,
  // does not combine with discount codes; free shipping is a separate rate).
  function closePop(pop) {
    if (!pop || pop.hidden) return;
    pop.hidden = true;
    var root = pop.closest('[data-offer-tiers]');
    if (root) Array.prototype.forEach.call(root.querySelectorAll('.offer-lad__tile[aria-expanded="true"]'), function (t) { t.setAttribute('aria-expanded', 'false'); });
  }
  function openPop(tile) {
    var root = tile.closest('[data-offer-tiers]');
    var pop = root && root.querySelector('[data-offer-pop]');
    if (!pop) return;
    if (tile.getAttribute('aria-expanded') === 'true') { closePop(pop); return; }
    closePop(pop);
    var rate = cur().rate;
    var min = parseFloat(tile.getAttribute('data-min')) * rate;
    var val = parseFloat(tile.getAttribute('data-val')) * rate;
    var freeMin = (parseFloat(pop.getAttribute('data-freeship-min')) || 75) * rate;
    var tiles = root.querySelectorAll('.offer-lad__tile');
    var best = 0;
    Array.prototype.forEach.call(tiles, function (t) {
      var m = parseFloat(t.getAttribute('data-min')) * rate;
      if (subtotal >= m) best = Math.max(best, parseFloat(t.getAttribute('data-val')) * rate);
    });
    var status, done = false;
    if (subtotal >= min && best > val) { status = 'Passed. Your bigger ' + fmt(best) + ' saving applies instead.'; done = true; }
    else if (subtotal >= min) { status = 'Unlocked. ' + fmt(val) + ' comes off at checkout.'; done = true; }
    else if (subtotal > 0) { status = 'Add ' + fmt(Math.round((min - subtotal) * 100) / 100) + ' more to unlock.'; }
    else { status = 'Spend ' + fmt(min) + ' or more to unlock.'; }
    var items = [
      'Applied automatically at checkout, no code needed.',
      'Counts your bag subtotal, before shipping and tax.',
      'One reward applies: the biggest level you reach.',
      'Cannot be combined with discount codes.'
    ];
    if (min >= freeMin) items.push('Ships free too, on orders from ' + fmt(freeMin) + '.');
    pop.querySelector('[data-offer-pop-body]').innerHTML =
      '<p class="offer-pop__h">' + fmt(val) + ' off orders of ' + fmt(min) + '+</p>' +
      '<p class="offer-pop__status' + (done ? ' is-done' : '') + '">' + status + '</p>' +
      '<ul>' + items.map(function (x) { return '<li>' + x + '</li>'; }).join('') + '</ul>' +
      '<a class="offer-pop__more" href="/pages/spend-and-save">See all levels</a>';
    pop.hidden = false;
    tile.setAttribute('aria-expanded', 'true');
    // Under the ladder, caret centred on the tile (the ladder may scroll sideways).
    var rootBox = root.getBoundingClientRect();
    var lad = tile.parentElement.getBoundingClientRect();
    var tb = tile.getBoundingClientRect();
    pop.style.top = (lad.bottom - rootBox.top + 9) + 'px';
    var caret = tb.left + tb.width / 2 - rootBox.left;
    pop.style.setProperty('--caret', Math.max(14, Math.min(rootBox.width - 14, caret)) + 'px');
  }
  function bindPop() {
    document.addEventListener('click', function (e) {
      var tile = e.target.closest && e.target.closest('.offer-lad__tile');
      if (tile) { openPop(tile); return; }
      if (e.target.closest && e.target.closest('[data-offer-pop-close]')) { closePop(e.target.closest('[data-offer-pop]')); return; }
      if (e.target.closest && e.target.closest('[data-offer-pop]')) return;
      Array.prototype.forEach.call(document.querySelectorAll('[data-offer-pop]:not([hidden])'), closePop);
    });
    // Swiping the tile row moves the tiles under the caret: close the details.
    document.addEventListener('scroll', function (e) {
      if (e.target && e.target.classList && e.target.classList.contains('offer-lad')) {
        var pop = e.target.closest('[data-offer-tiers]') && e.target.closest('[data-offer-tiers]').querySelector('[data-offer-pop]:not([hidden])');
        if (pop) closePop(pop);
      }
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') Array.prototype.forEach.call(document.querySelectorAll('[data-offer-pop]:not([hidden])'), closePop);
    });
  }

  function boot() { renderAll(); fetchCart(); watchCart(); watchDrawer(); bindPop(); }
  if (document.readyState !== 'loading') { boot(); }
  else document.addEventListener('DOMContentLoaded', boot);
})();
